import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Image, Users, UtensilsCrossed, Plus, X, Trash2, Edit2, 
  Save, Crown, ChefHat, Wine, Sparkles, User, GripVertical
} from 'lucide-react';
import './ContentManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentManager = () => {
  const [activeSection, setActiveSection] = useState('gallery');
  const [galleryImages, setGalleryImages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Form states
  const [galleryForm, setGalleryForm] = useState({
    url: '', title: '', subtitle: '', category: 'food', order: 0
  });
  const [teamForm, setTeamForm] = useState({
    name: '', role: '', description: '', image_url: '', icon_type: 'user', order: 0
  });
  const [menuForm, setMenuForm] = useState({
    name: '', description: '', price: '', category: 'american', subcategory: '', order: 0
  });

  const [error, setError] = useState(null);

  const loadGallery = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/content/gallery`);
      setGalleryImages(response.data);
    } catch (err) {
      console.error('Failed to load gallery:', err.message);
      setError('Unable to load gallery. Please try again.');
    }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/content/team`);
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Failed to load team:', err.message);
      setError('Unable to load team. Please try again.');
    }
  }, []);

  const loadMenu = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/content/menu`);
      setMenuItems(response.data);
    } catch (err) {
      console.error('Failed to load menu:', err.message);
      setError('Unable to load menu. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'gallery') loadGallery();
    else if (activeSection === 'team') loadTeam();
    else if (activeSection === 'menu') loadMenu();
  }, [activeSection, loadGallery, loadTeam, loadMenu]);

  // Gallery CRUD
  const handleAddGalleryImage = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/content/gallery/${editingItem.id}`, galleryForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/content/gallery`, galleryForm, { withCredentials: true });
      }
      resetForms();
      loadGallery();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save image');
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API}/content/gallery/${id}`, { withCredentials: true });
      loadGallery();
    } catch (err) {
      alert('Failed to delete image');
    }
  };

  // Team CRUD
  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/content/team/${editingItem.id}`, teamForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/content/team`, teamForm, { withCredentials: true });
      }
      resetForms();
      loadTeam();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save team member');
    }
  };

  const handleDeleteTeamMember = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      await axios.delete(`${API}/content/team/${id}`, { withCredentials: true });
      loadTeam();
    } catch (err) {
      alert('Failed to delete team member');
    }
  };

  // Menu CRUD
  const handleAddMenuItem = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/content/menu/${editingItem.id}`, menuForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/content/menu`, menuForm, { withCredentials: true });
      }
      resetForms();
      loadMenu();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save menu item');
    }
  };

  const handleDeleteMenuItem = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await axios.delete(`${API}/content/menu/${id}`, { withCredentials: true });
      loadMenu();
    } catch (err) {
      alert('Failed to delete menu item');
    }
  };

  const resetForms = () => {
    setShowAddModal(false);
    setEditingItem(null);
    setGalleryForm({ url: '', title: '', subtitle: '', category: 'food', order: 0 });
    setTeamForm({ name: '', role: '', description: '', image_url: '', icon_type: 'user', order: 0 });
    setMenuForm({ name: '', description: '', price: '', category: 'american', subcategory: '', order: 0 });
  };

  const startEdit = (item, type) => {
    setEditingItem(item);
    setShowAddModal(true);
    if (type === 'gallery') {
      setGalleryForm({ url: item.url, title: item.title, subtitle: item.subtitle || '', category: item.category, order: item.order || 0 });
    } else if (type === 'team') {
      setTeamForm({ name: item.name, role: item.role, description: item.description || '', image_url: item.image_url, icon_type: item.icon_type || 'user', order: item.order || 0 });
    } else if (type === 'menu') {
      setMenuForm({ name: item.name, description: item.description, price: item.price, category: item.category, subcategory: item.subcategory || '', order: item.order || 0 });
    }
  };

  const galleryCategories = [
    { value: 'all', label: 'All' },
    { value: 'food', label: 'Food' },
    { value: 'drinks', label: 'Drinks' },
    { value: 'events', label: 'Events' },
    { value: 'customers', label: 'Happy Customers' }
  ];

  const menuCategories = [
    { value: 'american', label: 'American Classics' },
    { value: 'indian-chicken', label: 'Indian - Chicken' },
    { value: 'indian-veg', label: 'Indian - Vegetarian' },
    { value: 'drinks', label: 'Drinks' }
  ];

  const iconTypes = [
    { value: 'crown', label: 'Crown (Owner)', icon: Crown },
    { value: 'chef', label: 'Chef Hat', icon: ChefHat },
    { value: 'wine', label: 'Wine Glass (Bartender)', icon: Wine },
    { value: 'sparkles', label: 'Sparkles (Team)', icon: Sparkles },
    { value: 'user', label: 'User', icon: User }
  ];

  const filteredGallery = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="content-manager" data-testid="content-manager">
      {/* Section Tabs */}
      <div className="cm-tabs">
        <button 
          className={`cm-tab ${activeSection === 'gallery' ? 'active' : ''}`}
          onClick={() => setActiveSection('gallery')}
        >
          <Image size={18} />
          Gallery
        </button>
        <button 
          className={`cm-tab ${activeSection === 'team' ? 'active' : ''}`}
          onClick={() => setActiveSection('team')}
        >
          <Users size={18} />
          Team
        </button>
        <button 
          className={`cm-tab ${activeSection === 'menu' ? 'active' : ''}`}
          onClick={() => setActiveSection('menu')}
        >
          <UtensilsCrossed size={18} />
          Menu
        </button>
      </div>

      {/* GALLERY SECTION */}
      {activeSection === 'gallery' && (
        <div className="cm-section">
          <div className="cm-header">
            <div>
              <h2>Gallery Images</h2>
              <p>Manage images shown in the Gallery section</p>
            </div>
            <button className="btn-add" onClick={() => setShowAddModal(true)}>
              <Plus size={18} />
              Add Image
            </button>
          </div>

          <div className="cm-filters">
            {galleryCategories.map(cat => (
              <button 
                key={cat.value}
                className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="cm-grid gallery-grid">
            {filteredGallery.length === 0 ? (
              <div className="empty-state">
                <Image size={48} />
                <p>No images in this category. Add some!</p>
              </div>
            ) : (
              filteredGallery.map(img => (
                <div key={img.id} className="gallery-card">
                  <div className="gallery-img-wrapper">
                    <img src={img.url} alt={img.title} />
                    <div className="gallery-overlay">
                      <button onClick={() => startEdit(img, 'gallery')}><Edit2 size={16} /></button>
                      <button onClick={() => handleDeleteGalleryImage(img.id)} className="delete"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <div className="gallery-info">
                    <h4>{img.title}</h4>
                    <span className="category-badge">{img.category}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add/Edit Gallery Modal */}
          {showAddModal && activeSection === 'gallery' && (
            <div className="modal-overlay" onClick={resetForms}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingItem ? 'Edit Image' : 'Add Gallery Image'}</h3>
                  <button onClick={resetForms}><X size={20} /></button>
                </div>
                <form onSubmit={handleAddGalleryImage}>
                  <div className="form-group">
                    <label>Image URL *</label>
                    <input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={galleryForm.url}
                      onChange={e => setGalleryForm({...galleryForm, url: e.target.value})}
                      required
                    />
                    {galleryForm.url && (
                      <img src={galleryForm.url} alt="Preview" className="image-preview" />
                    )}
                  </div>
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      placeholder="Image title"
                      value={galleryForm.title}
                      onChange={e => setGalleryForm({...galleryForm, title: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtitle</label>
                    <input
                      type="text"
                      placeholder="Optional subtitle"
                      value={galleryForm.subtitle}
                      onChange={e => setGalleryForm({...galleryForm, subtitle: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={galleryForm.category}
                      onChange={e => setGalleryForm({...galleryForm, category: e.target.value})}
                    >
                      <option value="food">Food</option>
                      <option value="drinks">Drinks</option>
                      <option value="events">Events</option>
                      <option value="customers">Happy Customers</option>
                    </select>
                  </div>
                  <button type="submit" className="btn-save">
                    <Save size={18} />
                    {editingItem ? 'Update Image' : 'Add Image'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TEAM SECTION */}
      {activeSection === 'team' && (
        <div className="cm-section">
          <div className="cm-header">
            <div>
              <h2>Team Members</h2>
              <p>Manage the "Meet the Team" section</p>
            </div>
            <button className="btn-add" onClick={() => setShowAddModal(true)}>
              <Plus size={18} />
              Add Member
            </button>
          </div>

          <div className="cm-grid team-grid">
            {teamMembers.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No team members added yet. Add your team!</p>
              </div>
            ) : (
              teamMembers.map(member => (
                <div key={member.id} className="team-card">
                  <div className="team-img-wrapper">
                    <img src={member.image_url} alt={member.name} />
                    <div className="team-overlay">
                      <button onClick={() => startEdit(member, 'team')}><Edit2 size={16} /></button>
                      <button onClick={() => handleDeleteTeamMember(member.id)} className="delete"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <div className="team-info">
                    <h4>{member.name}</h4>
                    <span className="role-badge">{member.role}</span>
                    {member.description && <p>{member.description}</p>}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add/Edit Team Modal */}
          {showAddModal && activeSection === 'team' && (
            <div className="modal-overlay" onClick={resetForms}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingItem ? 'Edit Team Member' : 'Add Team Member'}</h3>
                  <button onClick={resetForms}><X size={20} /></button>
                </div>
                <form onSubmit={handleAddTeamMember}>
                  <div className="form-group">
                    <label>Photo URL *</label>
                    <input
                      type="url"
                      placeholder="https://example.com/photo.jpg"
                      value={teamForm.image_url}
                      onChange={e => setTeamForm({...teamForm, image_url: e.target.value})}
                      required
                    />
                    {teamForm.image_url && (
                      <img src={teamForm.image_url} alt="Preview" className="image-preview round" />
                    )}
                  </div>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      placeholder="Full name or title"
                      value={teamForm.name}
                      onChange={e => setTeamForm({...teamForm, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <input
                      type="text"
                      placeholder="e.g., Owner & Founder, Head Chef"
                      value={teamForm.role}
                      onChange={e => setTeamForm({...teamForm, role: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      placeholder="Short bio or description"
                      value={teamForm.description}
                      onChange={e => setTeamForm({...teamForm, description: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Icon Type</label>
                    <div className="icon-selector">
                      {iconTypes.map(icon => {
                        const IconComp = icon.icon;
                        return (
                          <button
                            key={icon.value}
                            type="button"
                            className={`icon-option ${teamForm.icon_type === icon.value ? 'active' : ''}`}
                            onClick={() => setTeamForm({...teamForm, icon_type: icon.value})}
                          >
                            <IconComp size={20} />
                            <span>{icon.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button type="submit" className="btn-save">
                    <Save size={18} />
                    {editingItem ? 'Update Member' : 'Add Member'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MENU SECTION */}
      {activeSection === 'menu' && (
        <div className="cm-section">
          <div className="cm-header">
            <div>
              <h2>Menu Items</h2>
              <p>Manage food and drink menu items</p>
            </div>
            <button className="btn-add" onClick={() => setShowAddModal(true)}>
              <Plus size={18} />
              Add Item
            </button>
          </div>

          <div className="cm-filters">
            <button 
              className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </button>
            {menuCategories.map(cat => (
              <button 
                key={cat.value}
                className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="menu-items-list">
            {(selectedCategory === 'all' ? menuItems : menuItems.filter(i => i.category === selectedCategory)).length === 0 ? (
              <div className="empty-state">
                <UtensilsCrossed size={48} />
                <p>No menu items yet. Add your dishes!</p>
              </div>
            ) : (
              (selectedCategory === 'all' ? menuItems : menuItems.filter(i => i.category === selectedCategory)).map(item => (
                <div key={item.id} className="menu-item-card">
                  <div className="menu-item-info">
                    <div className="menu-item-header">
                      <h4>{item.name}</h4>
                      <span className="price">{item.price}</span>
                    </div>
                    <p>{item.description}</p>
                    <span className="category-badge">{item.category}</span>
                  </div>
                  <div className="menu-item-actions">
                    <button onClick={() => startEdit(item, 'menu')}><Edit2 size={16} /></button>
                    <button onClick={() => handleDeleteMenuItem(item.id)} className="delete"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add/Edit Menu Modal */}
          {showAddModal && activeSection === 'menu' && (
            <div className="modal-overlay" onClick={resetForms}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
                  <button onClick={resetForms}><X size={20} /></button>
                </div>
                <form onSubmit={handleAddMenuItem}>
                  <div className="form-group">
                    <label>Item Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., Chicken Tikka Masala"
                      value={menuForm.name}
                      onChange={e => setMenuForm({...menuForm, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description *</label>
                    <textarea
                      placeholder="Describe the dish..."
                      value={menuForm.description}
                      onChange={e => setMenuForm({...menuForm, description: e.target.value})}
                      required
                      rows={2}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Price *</label>
                      <input
                        type="text"
                        placeholder="$19"
                        value={menuForm.price}
                        onChange={e => setMenuForm({...menuForm, price: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        value={menuForm.category}
                        onChange={e => setMenuForm({...menuForm, category: e.target.value})}
                      >
                        {menuCategories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="btn-save">
                    <Save size={18} />
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentManager;
